import { api } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

const FORMATION = {
   default: "Individual",
   student_select: "Teams — Student pick",
   random: "Teams — Random Groups",
   instructor_assigned: "Teams — assigned",
   instructor_reusable: "Teams — assigned",
}

/*
=============================================================================
  DOWNLOAD BUTTON STATE  (added 2026-09-01)
=============================================================================
WHAT WENT WRONG. On 2026-09-01 students in both FIN 413 sections, David
included, reported that clicking Download "showed no activity", that repeated
clicking sometimes produced several copies of the file and sometimes only one,
and that refreshing the browser helped.

The database cause is fixed separately (event_table had no index, so a
60-second per-student poll was full-scanning 320,000 rows and saturating the
connection pool; see Download-Slowdown-DIAGNOSIS-AND-PLAN-2026-09-01.md). But
the reason it was INVISIBLE rather than merely slow is here in the client, and
it is three separate gaps:

  1. The file is fetched into a blob and saved with a synthetic <a>. That is a
     normal way to do an authenticated download, but it means the BROWSER HAS
     NO DOWNLOAD UI. No progress bar, no spinner, nothing. A student cannot
     tell a working download from a dead button.
  2. Nothing tracked whether a request was already running, so every extra
     click fired another complete request for the same multi-megabyte file.
     When they all completed, each fired its own save, which is where "several
     copies" came from.
  3. There was no timeout. A request that could not get a server connection
     waited forever rather than failing, so the button stayed dead
     indefinitely. Refreshing the page aborted those pending fetches, which is
     why refreshing appeared to help.

The three fixes below map one to one onto those.

DOWNLOAD_TIMEOUT_MS is deliberately generous. The largest assignment file is
about 2.3 MB, which is quick on campus wifi but not on a bad connection, and a
false "took too long" on a download that would have succeeded is worse than
waiting. It exists to convert an infinite hang into a message, not to enforce
a performance target.
*/
const DOWNLOAD_TIMEOUT_MS = 60000;

// Icon swapped in while a download is running. The button is a 32px square, so
// a spinner replaces the glyph; there is no room for a text label.
const SPINNER_SVG = `
   <svg class="btn-spinner" viewBox="0 0 24 24" width="16" height="16" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
   </svg>`;

class AppStudentClassDetail extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="student-class-detail">
            <div class="page-head">
               <button type="button" id="goback-btn" onclick="location.hash = '#classes'">← My Classes</button>
               <h1 id="cd-title">Loading...</h1>
               <p class="sub" id="cd-sub"></p>
            </div>
            <div class="cd-body">
               <!-- TA tools (2026-09-16). Hidden unless THIS class seats the viewer
                    as a TA and grants add_students. Adding only: removal stays
                    with the professor. The server re-checks either way. -->
               <div class="cd-section" id="sc-ta-add-section" hidden>
                  <div class="cd-section-head">
                     <h2>Add students</h2>
                  </div>
                  <div class="cd-panel">
                     <form id="sc-ta-add-form">
                        <label>Email addresses
                           <textarea name="emails" rows="4" autocomplete="off"
                                     placeholder="student@arizona.edu&#10;another@arizona.edu"></textarea>
                        </label>
                        <p class="dropzone-hint">One or many. Separate by new lines, commas, or spaces. Use the address they will sign in with; anyone new gets a sign-in account.</p>
                        <div class="dialog-actions">
                           <p class="msg" id="sc-ta-add-msg"></p>
                           <div class="dialog-buttons">
                              <button type="submit" class="btn-primary" id="sc-ta-add-btn">Add</button>
                           </div>
                        </div>
                     </form>
                  </div>
               </div>

               <!-- Games sits ABOVE Assignments and is hidden when empty. When a
                    game is running it is the only thing that matters on this
                    page, and the assignment it runs on is deliberately absent
                    from the list below until the game closes. -->
               <div class="cd-section" id="sc-games-section" hidden>
                  <div class="cd-section-head">
                     <h2>Games</h2>
                  </div>
                  <div id="sc-games" class="cd-panel"></div>
               </div>

               <div class="cd-section">
                  <div class="cd-section-head">
                     <h2>Assignments</h2>
                  </div>
                  <div id="sc-assignments" class="cd-panel"></div>
               </div>

               <!-- Modules: grouped resources for this class. Hidden entirely
                    when there is nothing to show, so every class that never
                    adopts a module looks exactly as it does today. -->
               <div class="cd-section" id="sc-modules-section" hidden>
                  <div class="cd-section-head">
                     <h2>Resources</h2>
                  </div>
                  <div id="sc-modules" class="cd-panel"></div>
               </div>

               <div class="cd-section">
                  <div class="cd-section-head">
                     <h2>Submission</h2>
                  </div>
                  <div id="sc-submissions" class="cd-panel"></div>
               </div>
            </div>
         </section>
      `;
      this.querySelector("#sc-ta-add-form").addEventListener("submit", (e) => this.onTaAddStudents(e));
   }

   /*
      Show "Add students" only where this class seats the viewer as a TA with
      add_students granted. /list-classes carries my_role and ta_permissions and
      returns nothing for an ordinary student, so a failure just leaves it hidden.
   */
   async loadTaTools() {
      const section = this.querySelector("#sc-ta-add-section");
      section.hidden = true;
      try {
         const res = await api("/list-classes");
         if (!res.ok) return;
         const data = await res.json();
         const c = (data.classes || []).find((x) => String(x.class_id) === String(this.classId));
         section.hidden = !(c && c.my_role === "ta" && c.ta_permissions && c.ta_permissions.add_students);
      } catch {
         /* leave it hidden */
      }
   }

   async onTaAddStudents(e) {
      e.preventDefault();
      const field = this.querySelector('#sc-ta-add-form [name="emails"]');
      const msg = this.querySelector("#sc-ta-add-msg");
      const btn = this.querySelector("#sc-ta-add-btn");
      const seen = new Set();
      const emails = (String(field.value || "").match(/[^\s,;<>"']+@[^\s,;<>"']+\.[^\s,;<>"']+/g) || [])
         .map((x) => x.trim().toLowerCase().replace(/[.,;]+$/, ""))
         .filter((x) => !seen.has(x) && seen.add(x));
      msg.className = "msg";
      if (!emails.length) {
         msg.textContent = "No email addresses found.";
         msg.className = "msg err";
         return;
      }
      btn.disabled = true;
      btn.textContent = "Adding...";
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/enrollments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ emails }),
         });
         const data = await res.json().catch(() => ({}));
         if (!res.ok || !data.ok) {
            throw new Error(data.reason === "ta_not_permitted"
               ? "This class does not let TAs add students."
               : "Couldn't add the students. Try again.");
         }
         const parts = [];
         if (data.added?.length) parts.push(`${data.added.length} added`);
         if (data.created?.length) parts.push(`${data.created.length} new account${data.created.length === 1 ? "" : "s"}`);
         if (data.already?.length) parts.push(`${data.already.length} already enrolled`);
         if (data.invalid?.length) parts.push(`${data.invalid.length} invalid`);
         msg.textContent = parts.join(" · ") || "Done.";
         msg.className = data.added?.length ? "msg ok" : "msg err";
         if (data.added?.length) field.value = "";
      } catch (err) {
         msg.textContent = err.message;
         msg.className = "msg err";
      } finally {
         btn.disabled = false;
         btn.textContent = "Add";
      }
   }

   async show(classId) {
      if (this.classId === classId) return;
      this.classId = classId;
      await this.render();
   }

   async render() {
      this.querySelector("#cd-title").textContent = "Loading...";
      this.querySelector("#cd-sub").textContent = "";
      this.querySelector("#sc-assignments").innerHTML = `<p class="empty">Loading...</p>`;
      this.querySelector("#sc-submissions").innerHTML = `<p class="empty">Loading...</p>`;

      try {
         const res = await api(`/me/classes/${encodeURIComponent(this.classId)}/assignments`);
         const data = await res.json();

         if (res.status === 403) {
            this.querySelector("#cd-title").textContent = "Class not found.";
            this.querySelector("#sc-assignments").innerHTML = `<p class="empty">You're not enrolled in this class.</p>`;
            this.querySelector("#sc-submissions").innerHTML = `<p class="empty">-</p>`;
            return;
         }
         if (!res.ok || !data.ok) throw new Error("failed");

         const c = data.class;
         this.querySelector("#cd-title").textContent = `${esc(c.course_code)} · ${esc(c.section)}`;
         this.querySelector("#cd-sub").textContent = `${esc(c.title || "")} — ${esc(c.term)}`;

         const assignments = data.assignments || [];
         this.renderAssignments(assignments);
         this.renderSubmissions(assignments);
         await this.loadGames();
         await this.loadModules();
         await this.loadTaTools();
      } catch {
         this.querySelector("#cd-title").textContent = "Couldn't load this class.";
         this.querySelector("#sc-assignments").innerHTML = `<p class="msg err">Couldn't load assignments</p>`;
         this.querySelector("#sc-submissions").innerHTML = `<p class="msg err">Couldn't load submissions</p>`;
      }
   }

   renderAssignments(assignments) {
      const el = this.querySelector("#sc-assignments");
      if (!assignments.length) {
         el.innerHTML = `<p class="empty">No assignments yet.</p>`;
         return;
      }

      el.innerHTML = `
         <ul class="cd-list">
            ${assignments.map((a) => `
               <li class="cd-item">
                  <div class="cd-item-main">
                     <div class="cd-item-name">${esc(a.title)}</div>
                     <div class="cd-item-meta">
                        ${a.has_file
                           ? `Due ${a.due_at ? new Date(a.due_at).toLocaleString() : "-"} · ${esc(FORMATION[a.formation_method] || FORMATION.default)}`
                           : `<span class="cd-warn">File Not Posted</span>`
                        }
                        ${a.is_closed
                           ? `<br><span class="cd-warn">Closed ${a.close_at
                                ? new Date(a.close_at).toLocaleString() : ""} - no longer downloadable</span>`
                           : ""}
                     </div>
                  </div>
                  <div class="cd-item-actions">
                     ${a.has_file && !a.is_closed ? `
                        <button type="button" class="btn-icon btn-download-assignment" data-id="${a.assignment_id}" aria-label="Download" title="Download">
                           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                           </svg>
                        </button>
                     ` : ""}
                  </div>
               </li>
            `).join("")}
         </ul>
      `;
      // Pass the button itself, so the handler can disable it and show a spinner
      // on it. Without the reference there is nothing to give feedback on.
      el.querySelectorAll(".btn-download-assignment").forEach((b) =>
         b.addEventListener("click", () => this.onDownloadAssignment(Number(b.dataset.id), b))
      );
   }

   renderSubmissions(assignments) {
      const el = this.querySelector("#sc-submissions");
      const submitted = assignments.filter((a) => Number(a.attempts) > 0);
      if (!submitted.length) {
         el.innerHTML = `<p class="empty">No submissions yet.</p>`;
         return;
      }

      el.innerHTML = `
         <ul class="cd-list">
            ${submitted.map((a) => {
               const n = Number(a.attempts);
               return `
                  <li class="cd-item">
                     <div class="cd-item-main">
                        <div class="cd-item-name">${esc(a.title)}</div>
                        <div class="cd-item-meta">
                           Best ${a.best_effective ?? "—"} · ${n} attempt${n === 1 ? "" : "s"}
                        </div>
                     </div>
                  </li>
               `}).join("")}
         </ul>
      `;
   }

   /*
      Live games in this class.

      Polls while the view is open, because the two things a student is waiting
      for - the file being released, then the game starting - are both flipped by
      the instructor seconds before they matter. Without a poll they would sit on
      a stale page and have to guess when to refresh.

      The section hides itself entirely when there are no games, so the ordinary
      class page is unchanged for every class that never runs one.
   */
   async loadGames() {
      const section = this.querySelector("#sc-games-section");
      const el = this.querySelector("#sc-games");
      try {
         const res = await api(`/me/classes/${encodeURIComponent(this.classId)}/games`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         const games = data.games || [];
         section.hidden = games.length === 0;
         if (!games.length) { this.stopGamePoll(); return; }
         this.renderGames(games);
         this.startGamePoll();
      } catch {
         section.hidden = true;
         this.stopGamePoll();
      }
   }

   startGamePoll() {
      if (this.gameTimer != null) return;
      this.gameTimer = window.setInterval(() => {
         // The router only sets `hidden` on the wrapper on the way out, so the
         // poll has to check for itself whether anyone is still looking.
         const view = this.closest("[data-view]");
         if (view && view.hidden) return;
         this.loadGames();
      }, 5000);
   }

   stopGamePoll() {
      if (this.gameTimer != null) { window.clearInterval(this.gameTimer); this.gameTimer = null; }
   }

   disconnectedCallback() { this.stopGamePoll(); }

   renderGames(games) {
      const el = this.querySelector("#sc-games");
      const STATE = {
         scheduled: ["Not started", "Your professor will start this shortly."],
         open: ["Running", "The clock is going. Work in the file."],
         paused: ["Paused", "Your professor paused the game."],
      };
      el.innerHTML = `
         <ul class="cd-list">
            ${games.map((g) => {
               const [label, note] = STATE[g.state] || ["", ""];
               return `
               <li class="cd-item">
                  <div class="cd-item-main">
                     <div class="cd-item-name">${esc(g.title)}</div>
                     <div class="cd-item-meta">
                        <span class="game-state-pill game-state-${esc(g.state)}">${esc(label)}</span>
                        ${esc(note)}
                        ${g.can_download ? "" : `<br><span class="cd-warn">File not released yet</span>`}
                     </div>
                  </div>
                  <div class="cd-item-actions">
                     ${g.can_download ? `
                        <button type="button" class="btn-primary btn-download-game" data-id="${g.session_id}">
                           Download
                        </button>` : ""}
                  </div>
               </li>`;
            }).join("")}
         </ul>`;
      el.querySelectorAll(".btn-download-game").forEach((b) =>
         b.addEventListener("click", () => this.onDownloadGame(Number(b.dataset.id), b))
      );
   }

   /*
   Guarded the same way as the assignment download. It carried the identical
   defect (no in-flight guard, no timeout, no feedback) and it matters MORE
   here, not less: this is the button students press at the start of a timed
   competition, all at the same instant, which is the worst possible moment for
   a dead-looking button and a burst of duplicate requests.
   */
   async onDownloadGame(sessionId, btn) {
      const res = await this._guardedFetch(
         `game:${sessionId}`,
         `/me/classes/${encodeURIComponent(this.classId)}/games/${sessionId}/download`,
         btn);
      if (!res) return;

      try {
         if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            alert(data.reason === "not_released"
               ? "Your professor hasn't released this file yet."
               : "Couldn't download the file.");
            await this.loadGames();     // re-sync: our view of the flag was stale
            return;
         }
         this._saveBlob(res, await res.blob(), "game.xlsm");
      } catch {
         alert("Couldn't download the file.");
      }
   }

   /*
      Modules attached to this class, with the resources this viewer may see IN
      THIS CLASS.

      ⭐ The server decides what comes back, and it decides per class. The same
      person can be a TA here and a plain student in another class, so the list
      is never cached across classes and nothing is filtered in the browser: what
      arrives is already what they are entitled to. Rendering only, no gating.
   */
   async loadModules() {
      const section = this.querySelector("#sc-modules-section");
      const el = this.querySelector("#sc-modules");
      try {
         const res = await api(`/me/classes/${encodeURIComponent(this.classId)}/modules`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");

         // A module with no visible resources is not worth a heading: to a
         // student it would read as an empty promise.
         const modules = (data.modules || []).filter((m) => (m.resources || []).length);
         section.hidden = modules.length === 0;
         if (!modules.length) return;

         el.innerHTML = modules.map((m) => `
            <div class="cd-module">
               <div class="cd-item-name">${esc(m.title)}</div>
               ${m.description ? `<div class="cd-item-meta">${esc(m.description)}</div>` : ""}
               <ul class="cd-list">
                  ${m.resources.map((r) => `
                     <li class="cd-item">
                        <div class="cd-item-main">
                           <div class="cd-item-name">${esc(r.title)}</div>
                           <div class="cd-item-meta">
                              ${esc({ pdf: "PDF", workbook: "Workbook", video_link: "Video", link: "Link" }[r.kind] || r.kind)}
                              ${r.audience === "instructor" ? ` · <span class="cd-warn">Instructor only</span>` : ""}
                           </div>
                        </div>
                        <div class="cd-item-actions">
                           <button type="button" class="btn-open-resource"
                                   data-id="${r.resource_id}" data-url="${esc(r.url || "")}">Open</button>
                        </div>
                     </li>`).join("")}
               </ul>
            </div>`).join("");

         el.querySelectorAll(".btn-open-resource").forEach((b) =>
            b.addEventListener("click", () => this.onOpenResource(Number(b.dataset.id), b.dataset.url)));
      } catch {
         section.hidden = true;
      }
   }

   async onOpenResource(resourceId, url) {
      if (url) { window.open(url, "_blank", "noopener"); return; }
      try {
         const res = await api(
            `/me/classes/${encodeURIComponent(this.classId)}/resources/${resourceId}/download`);
         if (!res.ok) {
            alert("This resource isn't available yet.");
            await this.loadModules();       // our view of the release was stale
            return;
         }
         const blob = await res.blob();
         const href = URL.createObjectURL(blob);
         const cd = res.headers.get("Content-Disposition") || "";
         const isInline = /^\s*inline/i.test(cd);
         if (isInline) {
            // A PDF is served inline because a guide is for reading. Revoke late:
            // revoking immediately can race the new tab's load.
            window.open(href, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(href), 60000);
            return;
         }
         const match = cd.match(/filename="?([^"]+)"?/);
         const a = document.createElement("a");
         a.href = href;
         a.download = match ? match[1] : "resource";
         document.body.appendChild(a);
         a.click();
         a.remove();
         URL.revokeObjectURL(href);
      } catch {
         alert("Couldn't open this resource.");
      }
   }

   /*
   THE IN-FLIGHT GUARD, shared by every download on this page.

   `key` identifies the thing being downloaded, so two DIFFERENT files can
   download at once while a second click on the SAME file is ignored. Keying on
   the button element instead would not survive a re-render, and keying on
   nothing at all would block a student who legitimately wants two files.

   Returns the Response on success, or null when it was suppressed or failed.
   The caller does not need to care which.
   */
   async _guardedFetch(key, url, btn) {
      this._downloading ??= new Set();
      if (this._downloading.has(key)) return null;   // fix 2: no duplicate requests
      this._downloading.add(key);

      // fix 1: the button says something is happening, since the browser will not
      const originalHtml = btn ? btn.innerHTML : null;
      if (btn) {
         btn.disabled = true;
         btn.setAttribute("aria-busy", "true");
         // A 32px icon button has no room for text, so swap the glyph. A button
         // that carries a text label keeps it and just gets the disabled style.
         if (btn.classList.contains("btn-icon")) btn.innerHTML = SPINNER_SVG;
         else btn.textContent = "Downloading...";
      }

      // fix 3: an unreachable server fails visibly instead of hanging forever
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), DOWNLOAD_TIMEOUT_MS);

      try {
         return await api(url, { signal: ctrl.signal });
      } catch (err) {
         alert(err?.name === "AbortError"
            ? "That took too long. Check your connection and try again."
            : "Couldn't download the file.");
         return null;
      } finally {
         /*
         EVERY path restores the button, including the abort and including an
         exception thrown by the caller while handling the response. A button
         left disabled by a failed download is worse than the bug this fixes,
         because the student then cannot retry at all.
         */
         clearTimeout(timer);
         this._downloading.delete(key);
         if (btn) {
            btn.disabled = false;
            btn.removeAttribute("aria-busy");
            if (originalHtml !== null) btn.innerHTML = originalHtml;
         }
      }
   }

   /* Save a fetched Response to disk. Split out so the three download handlers
      cannot drift apart in how they name or revoke the file. */
   _saveBlob(res, blob, fallbackName) {
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
   }

   async onDownloadAssignment(assignmentId, btn) {
      const res = await this._guardedFetch(
         `assignment:${assignmentId}`,
         `/me/classes/${encodeURIComponent(this.classId)}/assignments/${assignmentId}/download`,
         btn);
      if (!res) return;   // suppressed as a duplicate, or already reported

      try {
         if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            alert({
               not_found: "This assignment's file isn't available yet.",
               closed: "This assignment has closed and can no longer be downloaded.",
            }[data.reason] || "Couldn't download the file.");
            // Our view of the window was stale - re-read so the row updates.
            if (data.reason === "closed") await this.render();
            return;
         }

         // Binary Large OBject — a chunk of raw binary data held in memory
         this._saveBlob(res, await res.blob(), "assignment.xlsx");
      } catch {
         alert("Couldn't download the file.");
      }
   }
}

customElements.define("app-student-class-detail", AppStudentClassDetail);